import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useMatch, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { listProjects, updateProject, type Project } from '../lib/projects';
import { listAssignees, type Assignee } from '../lib/assignees';
import {
  createTask,
  listTaskTags,
  listTasks,
  nextPendingTaskId,
  type Task,
  type TaskSummary,
} from '../lib/tasks';
import { fetchTask, putTask } from '../lib/taskCache';
import { PinnedProjectRow } from '../components/projects/PinnedProjectRow';
import { DayTasksCard } from '../components/dashboard/DayTasksCard';
import { useDayTasks } from '../components/dashboard/useDayTasks';
import { FocusCard } from '../components/dashboard/FocusCard';
import { DailyChecklistCard } from '../components/daily/DailyChecklistCard';
import { DayStatsCard } from '../components/dashboard/DayStatsCard';
import { useDayStats } from '../components/dashboard/useDayStats';
import { useFocusSession } from '../components/dashboard/useFocusSession';
import { SidePanel } from '../components/tasks/SidePanel';
import { TaskDetail } from '../components/tasks/TaskDetail';
import type { CreateInBackground } from '../components/tasks/useTaskListState';
import {
  Alert,
  Card,
  EmptyState,
  LinkButton,
  PageHeader,
  SkeletonList,
} from '../components/ui';
import { FolderIcon } from '../components/icons';
import { useToast } from '../components/toast';

/**
 * Two questions, in the order they matter: what needs doing, and what am I
 * keeping an eye on.
 *
 * Pinned projects used to be the whole screen, back when there were no tasks to
 * put on it. They are now the narrower column -- still the standing context,
 * but not the thing you open the app to find out.
 */

/** What the task panel is handed, and what it writes back through. The day's
 *  list lives on this page rather than inside the card precisely so this can
 *  exist; see the comment on `useDayTasks`. */
type DashboardTaskContext = {
  tasks: TaskSummary[];
  syncTask: (task: Task) => void;
  removeTask: (taskId: string) => void;
};

const useDashboardTaskContext = () => useOutletContext<DashboardTaskContext>();

/** Local time, so the greeting matches the reader's clock rather than the
 *  server's. Boundaries are the conventional ones; nothing turns on them. */
function partOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/** Spelled out rather than numeric: this is a sentence under a greeting, not a
 *  field in a table. `undefined` locale means the reader's own. */
function today(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

export function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  /** Split for the reason spelled out on the tasks page: a failed unpin still
   *  has a list, and blanking it takes away the row that was just clicked. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pinningId, setPinningId] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * The two lists, told about each other's edits.
   *
   * They hold overlapping sets of the same tasks -- picking from the day's list
   * is the main way something gets into a session -- so ticking a task off in
   * one card has to reach the row for it in the other. Without this that list
   * goes on offering work that is already finished, which is the one thing a
   * list headed "Today's Tasks" must never do.
   *
   * Through a ref because the dependency is circular: each hook wants the
   * other's `syncTask`, and only one of them can be built first. The callbacks
   * below are stable and read whatever is in the box by the time they run, which
   * is the same trick both hooks already use internally for their own rows.
   */
  const crossSync = useRef<{
    day?: (task: Task) => void;
    focus?: (task: Task) => void;
    stats?: () => void;
  }>({});
  const day = useDayTasks({
    onTaskChanged: useCallback((task: Task) => {
      crossSync.current.focus?.(task);
      crossSync.current.stats?.();
    }, []),
  });
  const focus = useFocusSession({
    onTaskChanged: useCallback((task: Task) => {
      crossSync.current.day?.(task);
      crossSync.current.stats?.();
    }, []),
  });
  /**
   * The third participant, and the reason it is here rather than inside its own
   * card: ticking a task done in either list above moves "Tasks completed", and
   * a counter that ignores the click that should have moved it is worse than no
   * counter. It is told on every task edit rather than only on a completion --
   * the card debounces, and working out here which patches crossed the DONE
   * line would put that rule in a second place.
   */
  const stats = useDayStats();
  /** Today's win score, straight from the list that owns it -- see DayStatsCard. */
  const [wins, setWins] = useState<{ done: number; total: number } | null>(null);
  crossSync.current.day = day.syncTask;
  crossSync.current.focus = focus.syncTask;
  crossSync.current.stats = stats.refresh;

  /**
   * What the panel is handed, and what it writes back through.
   *
   * Both lists, not just the day's: a task can now sit in either or in both --
   * picked from the day's list, or added to the session by name and not on
   * today at all -- and an edit made in the panel has to reach whichever rows
   * are holding it.
   * Each hook ignores a task it does not have, so this fans out unconditionally
   * rather than working out which list to tell.
   *
   * `tasks` is only ever read as a seed lookup, so concatenating is safe and is
   * what lets a focus row that is not on today open the panel filled in rather
   * than on a spinner. Deduped by id because the overlap is the common case.
   */
  const focusTasks = focus.session?.tasks;
  const taskContext = useMemo<DashboardTaskContext>(() => {
    const byId = new Map<string, TaskSummary>();
    for (const task of day.tasks) byId.set(task.id, task);
    for (const task of focusTasks ?? []) byId.set(task.id, task);

    return {
      tasks: [...byId.values()],
      syncTask: (task) => {
        day.syncTask(task);
        focus.syncTask(task);
      },
      removeTask: (taskId) => {
        day.removeTask(taskId);
        focus.removeTask(taskId);
      },
    };
  }, [day.tasks, focusTasks, day.syncTask, day.removeTask, focus.syncTask, focus.removeTask]);

  // The open task's id lives in a descendant route, so useParams here would not
  // see it. Reading the path keeps the row marked under the panel -- the same
  // arrangement, and the same reason, as ProjectTasks.
  const activeTaskId = useMatch('/task/:taskId')?.params.taskId ?? null;

  useEffect(() => {
    const controller = new AbortController();

    listProjects({ pinned: true }, controller.signal)
      .then((data) => {
        setProjects(data.projects);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : 'Could not load pinned projects.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  /** This list is pinned-only, so unpinning drops the card rather than re-sorting it. */
  async function unpin(project: Project) {
    setPinningId(project.id);
    try {
      await updateProject(project.id, { pinned: false });
      setProjects((current) => current.filter((item) => item.id !== project.id));
      setActionError(null);
      // This list is pinned-only, so the row vanishes -- which is also exactly
      // what a failed request would look like from here.
      toast(`Unpinned ${project.name}`);
    } catch (toggleError: unknown) {
      setActionError(
        toggleError instanceof Error ? toggleError.message : 'Could not update this project.',
      );
    } finally {
      setPinningId(null);
    }
  }

  return (
    <div>
      {/*
        The subtitle used to read "You are signed in as name@example.com" --
        developer copy in the one line of the app somebody actually looks at
        first, answering a question the account block in the sidebar already
        answers. The date is the thing worth knowing on a screen whose subject
        is what is on today.
      */}
      <PageHeader
        title={`Good ${partOfDay()}${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        description={today()}
      />

      {/* Below `lg` the two stack, the day's work first: on a narrow screen the
          ordering question is the same one the widths answer here. */}
      <div className="mt-8 grid items-start gap-6 lg:grid-cols-3">
        {/* Above Today's Tasks, and in the same column. The order is the question order:
            what am I doing now, then what else needs doing -- and the second one
            is where the first one's rows come from. */}
        <div className="grid gap-6 lg:col-span-2">
          <FocusCard
            focus={focus}
            activeTaskId={activeTaskId}
            onEnded={(message) => toast(message, 'success')}
          />

          <DayTasksCard
            day={day}
            activeTaskId={activeTaskId}
            focusedIds={focus.taskIds}
            onToggleFocus={(task) =>
              void (focus.taskIds.has(task.id) ? focus.remove(task.id) : focus.add(task))
            }
          />
        </div>

        {/* The day first, then the standing context. The order is how long
            each answer stays true: the numbers are only about today, the win
            list is today's own, and the pinned projects were already there
            last month and will be next month. Pinned used to lead this column
            back when it was the whole screen; it reads better last now that
            there is something above it that changes by lunchtime. */}
        <div className="grid gap-6">
          <DayStatsCard stats={stats} wins={wins} />

          <DailyChecklistCard onWins={setWins} />

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3">
              <h2 className="text-lg font-semibold tracking-tight">Pinned</h2>
              <Link
                to="/projects"
                className="rounded text-sm font-medium text-honey-700 hover:text-honey-800 hover:underline"
              >
                Browse all
              </Link>
            </div>

            {actionError ? (
              <div className="border-b border-stone-100 p-3">
                <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
              </div>
            ) : null}

            {loadError ? (
              <div className="p-4">
                <Alert>{loadError}</Alert>
              </div>
            ) : loading ? (
              <SkeletonList rows={3} bordered={false} />
            ) : projects.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  compact
                  icon={<FolderIcon className="h-5 w-5" />}
                  title="Nothing pinned"
                  action={
                    <LinkButton compact to="/projects">
                      Browse projects
                    </LinkButton>
                  }
                >
                  Pin a project to keep it here.
                </EmptyState>
              </div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {projects.map((project) => (
                  <PinnedProjectRow
                    key={project.id}
                    project={project}
                    pinning={pinningId === project.id}
                    onTogglePin={(target) => void unpin(target)}
                  />
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {/* The panel renders here but is `fixed`, so it escapes this column. */}
      <Outlet context={taskContext} />
    </div>
  );
}

/**
 * The task panel, over the dashboard.
 *
 * A sibling of the project tab's and the Tasks page's rather than a shared one,
 * for the reason those two are already siblings: what differs between them is
 * where Close goes, where a task link points, and what the pickers are allowed
 * to offer. This one closes to `/`, keeps every link on `/task/:id` so following
 * a parent or a prerequisite does not dump you into a project, and has no
 * compose half -- nothing on the dashboard files a new task.
 *
 * It also has no list to lean on. The pages hand TaskDetail the rows they are
 * already holding; the day's list is a handful of tasks from every project, which
 * is the wrong set for a parent picker and no help at all to a prerequisite
 * one. So this fetches the open task's own project when it opens -- three
 * requests, only ever on a task somebody actually clicked.
 */
export function DashboardTaskPanelRoute() {
  const { taskId } = useParams<{ taskId: string }>();
  const { tasks, syncTask, removeTask } = useDashboardTaskContext();
  const navigate = useNavigate();
  const titleId = useId();

  const seed = tasks.find((task) => task.id === taskId);

  const [projectId, setProjectId] = useState<string | null>(null);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [projectTasks, setProjectTasks] = useState<TaskSummary[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  /**
   * Read through a ref rather than depended on.
   *
   * The day's row is what saves this a round trip, but it arrives whenever the
   * card's fetch lands -- so keying the effect on it would re-run the whole
   * load a moment after a deep link had already done it, for the same three
   * answers. The effect runs once per task and takes whatever the seed was at
   * that moment.
   */
  const seedProjectId = useRef<string | undefined>(undefined);
  seedProjectId.current = seed?.projectId;

  useEffect(() => {
    if (!taskId) return;
    const controller = new AbortController();

    void (async () => {
      try {
        // From the row when there is one; otherwise from the detail itself.
        // `fetchTask` shares one request between everyone asking for a task, and
        // TaskDetail is asking for this one right now -- so the fallback costs
        // a wait rather than a request.
        const id = seedProjectId.current ?? (await fetchTask(taskId)).projectId;
        if (controller.signal.aborted) return;
        setProjectId(id);

        // All three are suggestions, and all three fail silently: an empty list
        // degrades to a usable control, which is the same call ProjectTasks
        // makes about the same three requests.
        await Promise.all([
          // Archived people included on purpose: the picker needs whoever is
          // already on a task even after they leave, or the select renders
          // blank and one arrow key silently unassigns it.
          listAssignees({ includeArchived: true }, controller.signal).then((data) =>
            setAssignees(data.assignees),
          ),
          // The open task's own project, never the day's list. The server rejects
          // a cross-project parent outright, so anything wider would be
          // offering choices it knows will fail.
          listTasks(id, {}, controller.signal).then((data) => setProjectTasks(data.tasks)),
          listTaskTags(id, controller.signal).then((data) => setTagSuggestions(data.tags)),
        ]);
      } catch {
        // Deliberately quiet. The panel's own load reports a task that cannot
        // be fetched; this only decides how full the pickers are.
      }
    })();

    return () => controller.abort();
  }, [taskId]);

  /**
   * Create a subtask without waiting for it.
   *
   * The pages put an optimistic row in the list and swap it when the create
   * lands. There is nothing to put one in here -- a new subtask is not on
   * today, and the card would be wrong to show it -- so this posts and
   * answers, and TaskDetail shows the pending entry in its own subtask section
   * off the id returned here. A failure answers null, which is what makes its
   * inline Try again appear.
   */
  const createInBackground = useCallback<CreateInBackground>(
    (draft) => {
      const pendingId = nextPendingTaskId();
      const { projectId: draftProjectId, ...fields } = draft;
      const target = draftProjectId ?? projectId;

      // Only reachable before the project is known, which is before the panel
      // has drawn the composer that calls this.
      if (!target) return { pendingId, result: Promise.resolve(null) };

      const result = createTask(target, fields)
        .then(({ task }) => {
          // The likeliest next stop for a subtask somebody just typed is the
          // subtask itself, and this is the whole of its detail already in hand.
          putTask(task);
          return task;
        })
        .catch(() => null);

      return { pendingId, result };
    },
    [projectId],
  );

  // Every link inside the panel stays on the dashboard: a parent, a subtask or
  // a prerequisite opens here rather than navigating into its project.
  const hrefFor = useCallback((id: string) => `/task/${id}`, []);

  if (!taskId) return null;

  return (
    <SidePanel labelledBy={titleId} focusKey={taskId} onClose={() => navigate('/')}>
      <TaskDetail
        // Remounted per task: params change without remounting the route
        // element, so an open title editor or a half-typed comment would
        // otherwise carry over to the next task.
        key={taskId}
        taskId={taskId}
        seed={seed}
        tasks={projectTasks}
        assignees={assignees}
        tagSuggestions={tagSuggestions}
        titleId={titleId}
        hrefFor={hrefFor}
        createInBackground={createInBackground}
        onTaskChanged={syncTask}
        onTaskDeleted={removeTask}
      />
    </SidePanel>
  );
}
